import React, { useCallback, useEffect, useRef, useState } from 'react';
import initializeFirebase from '../../../firebaseConfig';
import { increment, off, onValue, push, ref, update } from 'firebase/database';
import { useSelector } from 'react-redux';
import { getCurrentUTCTimestamp } from '../../../utils/utils';
import LiveChatCommentItem from './LiveChatCommentItem';
import { commentGifUploadThumbnail, commentImageUploadThumbnail } from '../../VideoPlayer/service';
import { useTranslateText } from '../../../Components/Multilanguage/Translate';
import Translate from '../../../Components/Multilanguage/Translate';
import { toast } from 'react-toastify';
import GiphySearchbox from 'react-giphy-searchbox';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import Gif from '@mui/icons-material/Gif';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const LiveChat = ({ videoId, projectInfo }) => {
  const user = useSelector((state) => state?.user?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);

  const appInfo = { projectDetails: projectInfo, accessToken };
  const gifApiKey = projectInfo?.projectConfig?.config?.GIPHY_API_KEY;
  const translateText = useTranslateText();

  const [comments, setComments] = useState([]);
  const [commentInputValue, setCommentInputValue] = useState('');
  const [selectedGif, setSelectedGif] = useState(null);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [isGiphyModalOpen, setIsGiphyModalOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const isAtBottomRef = useRef(true);

  const scrollToBottom = useCallback((smooth = false) => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const behavior = smooth ? 'smooth' : 'auto';

    const doScroll = () => {
      const last = el.lastElementChild;
      if (last && typeof last.scrollIntoView === 'function') {
        try {
          last.scrollIntoView({ behavior, block: 'end', inline: 'nearest' });
        } catch (e) {
          // fallback
          el.scrollTop = el.scrollHeight - el.clientHeight;
        }
      } else {
        el.scrollTop = el.scrollHeight - el.clientHeight;
      }
    };

    requestAnimationFrame(() => {
      doScroll();
      setTimeout(doScroll, 80);
      setTimeout(doScroll, 220);
    });
  }, []);


  useEffect(() => {
    if (!videoId || !projectInfo) return;
    const firebaseDB = initializeFirebase(projectInfo);
    if (!firebaseDB) return;
    const dbRef = ref(firebaseDB, `comments/event-${videoId}`);
    onValue(dbRef, (snapshot) => {
      if (snapshot.exists()) {
        const commentArray = [];
        snapshot.forEach((child) => {
          commentArray.push({ ...child.val(), id: child.key, videoId });
        });
        setComments(commentArray.filter((c) => c.delete_status !== true));
      } else {
        setComments([]);
      }
    });
    return () => off(dbRef);
  }, [videoId, projectInfo]);

  useEffect(() => {
    if (isAtBottomRef.current) scrollToBottom(false);
  }, [comments, scrollToBottom]);

  useEffect(() => {
    if (!replyTarget) scrollToBottom(false);
  }, [replyTarget, scrollToBottom]);

  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    isAtBottomRef.current = atBottom;
    setShowScrollBtn(!atBottom);
  };

  const onGifSelect = async (item) => {
    const gifUrl = item?.images?.fixed_width_small?.url;
    const response = await commentGifUploadThumbnail(gifUrl, appInfo);
    if (response?.data?.data) {
      setImageDimensions({
        height: parseInt(item.images.fixed_width_small.height),
        width: parseInt(item.images.fixed_width_small.width),
        url: response.data.data,
      });
      setSelectedGif(item);
      setImage(null);
      setImagePreview(null);
      setIsGiphyModalOpen(false);
    }
  };

  const handleFileChange = async (e) => {
    const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.gif)$/i;
    if (!allowedExtensions.exec(e?.target?.files[0]?.name)) return;
    const files = e.target.files;
    setSelectedGif(null);
    const response = await commentImageUploadThumbnail(files[0], appInfo);
    if (response?.data?.data) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new window.Image();
        img.onload = () => {
          setImageDimensions({ width: img.width, height: img.height, url: response.data.data });
        };
        img.src = reader.result;
        setImagePreview(reader.result);
      };
      setImage(files[0]);
      reader.readAsDataURL(files[0]);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  const handleRemoveSelection = () => {
    setSelectedGif(null);
    setImage(null);
    setImagePreview(null);
    setImageDimensions(null);
  };

  const postCommentHandler = async () => {
    if (!commentInputValue && !selectedGif && !image) {
      toast.error(<Translate textKey={'comment_or_image_required'} />, { position: 'top-center' });
      return;
    }
    const firebaseDB = initializeFirebase(projectInfo);
    if (!firebaseDB) return;

    if (replyTarget) {
      const reply_data = {
        comment_text: commentInputValue || '',
        user_id: Number(user),
        created_at: getCurrentUTCTimestamp(),
      };
      if (imageDimensions) reply_data.image = imageDimensions;
      const dbRefParent = ref(firebaseDB, `comments/event-${videoId}/${replyTarget.data.id}`);
      update(dbRefParent, { reply_count: increment(1) });
      await push(ref(firebaseDB, `comments/event-${videoId}/${replyTarget.data.id}/replies`), reply_data);
    } else {
      const comment_data = {
        comment_text: commentInputValue || '',
        user_id: Number(user),
        created_at: getCurrentUTCTimestamp(),
      };
      if (imageDimensions) comment_data.image = imageDimensions;
      await push(ref(firebaseDB, `comments/event-${videoId}`), comment_data);
      isAtBottomRef.current = true;
      scrollToBottom();
    }

    setCommentInputValue('');
    setSelectedGif(null);
    setImage(null);
    setImagePreview(null);
    setImageDimensions(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      postCommentHandler();
    }
  };

  return (
    <div className="liveChatPanel">
      <div className="chatHeader">
        <span className="liveDot" />
        <span className="chatHeaderTitle"><Translate textKey={'live_chat'} /></span>
        {comments.length > 0 && <span className="chatCount">{comments.length} <Translate textKey={'comments'} /></span>}
      </div>

      <div className="chatMessagesWrapper">
        {replyTarget ? (() => {
          const liveData = comments.find((c) => c.id === replyTarget.data.id) || replyTarget.data;
          return (
          <div className="replyModeView">
            <div className="replyModeHeader">
              <button className="replyModeBackBtn" onClick={() => setReplyTarget(null)}>
                <ArrowBackIcon fontSize="small" />
              </button>
            </div>
            <div className="replyModeComment">
              <LiveChatCommentItem
                key={liveData.id}
                data={liveData}
                isEvent={true}
                projectInfo={projectInfo}
                hideReplyAction={true}
                onMediaLoad={() => scrollToBottom(false)}
                onDeleted={() => setReplyTarget(null)}
              />
            </div>
          </div>
          );
        })() : (
          <div className="chatMessages" ref={messagesContainerRef} onScroll={handleScroll}>
            {comments.length === 0 && (
              <p className="chatEmptyMessage"><Translate textKey={'no_messages'} /></p>
            )}
            {comments.map((item) => (
              <LiveChatCommentItem
                key={item.id}
                data={item}
                isEvent={true}
                projectInfo={projectInfo}
                onReply={(data, authorName) => setReplyTarget({ data, authorName })}
                onMediaLoad={() => scrollToBottom(false)}
              />
            ))}
          </div>
        )}
        {showScrollBtn && !replyTarget && (
          <button
            className="scrollToBottomBtn"
            onClick={() => { isAtBottomRef.current = true; scrollToBottom(true); setShowScrollBtn(false); }}
            aria-label="Scroll to latest"
          >
            <KeyboardArrowDownIcon />
          </button>
        )}
      </div>


      {user ? (
        <div className="chatInputArea">
          {(selectedGif || imagePreview) && (
            <div className="chatMediaPreview">
              <button className="removeMediaBtn" onClick={handleRemoveSelection}>
                <CloseIcon fontSize="small" />
              </button>
              <img
                src={selectedGif ? selectedGif.images.fixed_height.url : imagePreview}
                alt="preview"
              />
            </div>
          )}

          <div className="chatInputRow">
            <input
              className="chatInput"
              type="text"
              placeholder={replyTarget
                ? `${translateText('reply_to')} ${replyTarget.authorName}…`
                : translateText('add_your_comment')}
              value={commentInputValue}
              onChange={(e) => setCommentInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={500}
            />
            <label className="mediaBtn" title="Upload image">
              <PhotoCamera fontSize="small" />
              <input type="file" accept="image/*" onChange={handleFileChange} ref={fileInputRef} />
            </label>
            <button className="mediaBtn gifBtn" title="Add GIF" onClick={() => setIsGiphyModalOpen(true)}>
              <Gif />
            </button>
            <button
              className="sendBtn"
              onClick={postCommentHandler}
              disabled={!commentInputValue.trim() && !selectedGif && !image}
              aria-label="Send"
            >
              <SendIcon fontSize="small" />
            </button>
          </div>
        </div>
      ) : (
        <div className="chatSignIn">
          <span><Translate textKey={'sign_in_to_chat'} /></span>
        </div>
      )}

      {isGiphyModalOpen && (
        <div className="chatGiphyOverlay" onClick={(e) => { if (e.target === e.currentTarget) setIsGiphyModalOpen(false); }}>
          <div className="chatGiphyContent">
            <button className="closeGiphyBtn" onClick={() => setIsGiphyModalOpen(false)}>
              <CloseIcon />
            </button>
            <GiphySearchbox
              apiKey={gifApiKey}
              onSelect={onGifSelect}
              searchPlaceholder={translateText('search')}
              masonryConfig={[
                { columns: 2, imageWidth: 110, gutter: 5 },
                { mq: '700px', columns: 3, imageWidth: 120, gutter: 5 },
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveChat;
